import { createSignal, Show, onCleanup } from 'solid-js';
import type { ComparisonStatsResponse } from '@/services/selfservice';

interface MotivationalPopupProps {
  stats: ComparisonStatsResponse;
  correctAnswers: number;
  totalTiles: number;
  onClose: () => void;
}

export default function MotivationalPopup(props: MotivationalPopupProps) {
  const [isVisible, setIsVisible] = createSignal(true);
  const [isAnimating, setIsAnimating] = createSignal(true);

  // Auto-hide after 6 seconds
  const timeout = setTimeout(() => {
    handleClose();
  }, 6000);

  onCleanup(() => {
    clearTimeout(timeout);
  });

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      props.onClose();
    }, 300);
  };

  const getPaceColor = () => {
    if (props.stats.insufficient_data) return 'from-blue-400 to-cyan-500';
    switch (props.stats.pace_message) {
      case 'ahead': return 'from-green-400 to-emerald-500';
      case 'on_track': return 'from-yellow-400 to-amber-500';
      case 'behind': return 'from-orange-400 to-red-500';
      default: return 'from-blue-400 to-cyan-500';
    }
  };

  const getBorderColor = () => {
    if (props.stats.insufficient_data) return 'border-blue-500/30';
    switch (props.stats.pace_message) {
      case 'ahead': return 'border-emerald-500/30';
      case 'on_track': return 'border-yellow-500/30';
      case 'behind': return 'border-orange-500/30';
      default: return 'border-blue-500/30';
    }
  };

  const getEmoji = () => {
    if (props.stats.insufficient_data) return '🌟';
    switch (props.stats.pace_message) {
      case 'ahead': return '🔥';
      case 'on_track': return '👍';
      case 'behind': return '💪';
      default: return '📊';
    }
  };

  const getMessage = () => {
    const s = props.stats;
    if (s.insufficient_data) {
      return {
        main: 'Jesteście jednym z pierwszych zespołów!',
        sub: 'Bawcie się dobrze i ustalcie najlepszy czas!'
      };
    }

    const yours = s.at_current_time?.your_correct ?? props.correctAnswers;
    const avg = s.at_current_time?.avg_correct ?? 0;
    const percentile = s.estimated_percentile ?? 50;

    // Milestone messages based on completion
    const pct = props.totalTiles > 0 ? Math.round((props.correctAnswers / props.totalTiles) * 100) : 0;

    if (pct >= 100) {
      return {
        main: 'Wszystkie zagadki rozwiązane!',
        sub: 'Szybko - wprowadźcie KOD STOPU!'
      };
    }
    if (pct >= 75) {
      const remaining = props.totalTiles - props.correctAnswers;
      return {
        main: `Już prawie! Zostało ${remaining} zagadek.`,
        sub: s.overall?.best_time_formatted
          ? `Najszybszy czas to ${s.overall.best_time_formatted}. Dasz radę szybciej?`
          : 'Dajcie z siebie wszystko!'
      };
    }

    // Pace-based messages
    switch (s.pace_message) {
      case 'ahead':
        return {
          main: `Macie ${yours} zagadek - lepiej niż ${percentile}% drużyn!`,
          sub: 'Świetne tempo, tak trzymajcie!'
        };
      case 'on_track':
        return {
          main: `Świetne tempo!`,
          sub: `Inne drużyny w tym momencie miały średnio ${avg.toFixed(0)} zagadek.`
        };
      case 'behind':
        return {
          main: `Inne drużyny miały średnio ${avg.toFixed(0)} zagadek w tym czasie.`,
          sub: `Macie ${yours} - przyśpieszcie!`
        };
      default:
        return {
          main: `Macie ${yours} rozwiązanych zagadek.`,
          sub: `Średnia to ${avg.toFixed(0)} w tym momencie gry.`
        };
    }
  };

  const message = getMessage();

  // Comparison bar width
  const getBarWidths = () => {
    const s = props.stats;
    if (s.insufficient_data || !s.at_current_time) return { yours: 50, avg: 50 };
    const max = Math.max(s.at_current_time.your_correct, s.at_current_time.avg_correct, 1);
    return {
      yours: Math.round((s.at_current_time.your_correct / max) * 100),
      avg: Math.round((s.at_current_time.avg_correct / max) * 100)
    };
  };

  return (
    <Show when={isVisible()}>
      <div
        class={`fixed top-4 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ${
          isAnimating() ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
        }`}
      >
        <div class={`bg-gray-800 border ${getBorderColor()} rounded-2xl shadow-2xl overflow-hidden min-w-[300px] max-w-sm`}>
          {/* Gradient top bar */}
          <div class={`h-1.5 bg-gradient-to-r ${getPaceColor()}`} />

          <div class="p-4">
            <div class="flex items-start gap-3">
              {/* Emoji */}
              <div class="text-3xl animate-bounce flex-shrink-0">
                {getEmoji()}
              </div>

              {/* Content */}
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between mb-1">
                  <span class="text-sm font-bold text-white truncate pr-2">
                    {message.main}
                  </span>
                  <button
                    onClick={handleClose}
                    class="text-gray-500 hover:text-white transition-colors flex-shrink-0"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p class="text-xs text-gray-400">
                  {message.sub}
                </p>

                {/* Comparison bar - only show when we have data */}
                <Show when={!props.stats.insufficient_data && props.stats.at_current_time}>
                  <div class="mt-2 space-y-1">
                    <div class="flex items-center gap-2 text-[10px]">
                      <span class="text-emerald-400 w-8">Wy</span>
                      <div class="flex-1 bg-gray-700 rounded-full h-2">
                        <div
                          class="bg-emerald-400 h-2 rounded-full transition-all duration-500"
                          style={`width: ${getBarWidths().yours}%`}
                        />
                      </div>
                      <span class="text-emerald-400 w-4 text-right">{props.stats.at_current_time!.your_correct}</span>
                    </div>
                    <div class="flex items-center gap-2 text-[10px]">
                      <span class="text-gray-400 w-8">Śr.</span>
                      <div class="flex-1 bg-gray-700 rounded-full h-2">
                        <div
                          class="bg-gray-500 h-2 rounded-full transition-all duration-500"
                          style={`width: ${getBarWidths().avg}%`}
                        />
                      </div>
                      <span class="text-gray-400 w-4 text-right">{props.stats.at_current_time!.avg_correct.toFixed(0)}</span>
                    </div>
                  </div>
                </Show>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Show>
  );
}
